import React from 'react';
import dayjs from 'dayjs';

import { useGetDomainsQuery } from '~features/domain-manager/api/domainManagerApiSlice';
import { useHasCrawlTrafficQuery } from '~features/dashboard-v3/DashboardV3API';

import { usePrerenderUser, useIsSubscriptionCancelled } from '../../hooks/usePrerenderUser';
import { useUserSnap } from './UserSnap';

/**
 * Usersnap event that opens the survey aimed at the established paying user base.
 * Which survey it opens is configured on the Usersnap side, so the same trigger can
 * be repurposed for the next survey without a code change.
 */
export const PAID_USER_SURVEY_EVENT = 'survey-paid-users';

export const SESSION_STORAGE_KEY = 'usersnap.survey-paid-users.opened';
const ONE_DAY = 24 * 60 * 60 * 1000; // milliseconds in one day
const MINIMUM_INTEGRATION_DAYS = 60;
const CRAWL_TRAFFIC_WINDOW_DAYS = 30;
// Let the page settle before the survey appears, so it does not land in the user's face.
export const TRIGGER_DELAY_MS = 20 * 1000;

const daysSince = (date: Date) => (Date.now() - date.getTime()) / ONE_DAY;

function alreadyOpenedThisSession() {
  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * The audience agreed for PRE-3569: paying customers who have been live long enough
 * to have formed habits around the dashboard filters, and who still get crawled.
 */
export const usePaidUserSurveyAudience = (): boolean => {
  const user = usePrerenderUser();
  const isSubscriptionCancelled = useIsSubscriptionCancelled();

  // Read once per mount, not per render. The trigger records that the survey
  // opened while its delay is still counting down; a live read would flip the
  // queries to skip mid-flight, drop their data, turn eligibility false and
  // cancel the pending timer — so the survey would never appear.
  const [openedBeforeThisMount] = React.useState(alreadyOpenedThisSession);

  const isEstablished =
    !!user.trackingCodeInstalledDate && daysSince(user.trackingCodeInstalledDate) > MINIMUM_INTEGRATION_DAYS;

  // Cheap, already-loaded criteria first: everything below costs a request.
  const matchesAccountCriteria =
    user.hasSession && !user.isTrial && !isSubscriptionCancelled && isEstablished && !alreadyOpenedThisSession();

  const DomainsResult = useGetDomainsQuery('', { skip: !matchesAccountCriteria });
  const hasDomain = (DomainsResult.data?.totalCount ?? 0) > 0;

  const CrawlTrafficResult = useHasCrawlTrafficQuery(
    {
      dateFrom: dayjs().subtract(CRAWL_TRAFFIC_WINDOW_DAYS, 'day').startOf('day').toDate(),
      dateTo: dayjs().endOf('day').toDate(),
    },
    { skip: !matchesAccountCriteria || !hasDomain }
  );
  const hasRecentCrawlTraffic = CrawlTrafficResult.data?.hasTraffic ?? false;

  return matchesAccountCriteria && hasDomain && hasRecentCrawlTraffic;
};

const rememberOpenedThisSession = () => {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
  } catch {
    // A browser that refuses session storage only costs us the once-per-session guard.
  }
};

/**
 * Renders nothing. Mount it on the page the survey asks about — it fires the
 * Usersnap trigger once per session for the eligible audience. How often the survey
 * is shown again after a skip, and that it stops after a submission, is configured
 * in the Usersnap project.
 */
const PaidUserSurveyTrigger = () => {
  const { api } = useUserSnap();
  const isEligible = usePaidUserSurveyAudience();

  React.useEffect(() => {
    if (!api || !isEligible || alreadyOpenedThisSession()) return undefined;

    // Recorded when the survey opens, not when the timer is armed: leaving the
    // page inside the delay would otherwise spend the session on a survey
    // nobody saw.
    const timeout = setTimeout(() => {
      rememberOpenedThisSession();
      api.logEvent(PAID_USER_SURVEY_EVENT);
    }, TRIGGER_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [api, isEligible]);

  return null;
};

export default PaidUserSurveyTrigger;
